import c from 'classnames';
import { useMemo, useState } from 'react';

import { BacteriumType } from 'types/Insights';
import { KitType } from 'types/Kit';

import { Icon } from '@repo/ui';
import { AssociationsList, Markdown } from 'components';
import { AnimatePresence, motion } from 'framer-motion';
import styles from './Bacterium.module.css';

function detailsPresent(bacterium: BacteriumType, type: KitType) {
  //has associations in any form
  const hasAssociations =
    bacterium.report_taxa_categories?.gut_vaginal_health?.some(
      gvh => gvh.gut_vaginal_health_associations?.length,
    ) ||
    (bacterium.report_taxa_categories as any)?.associations_list?.length > 0;
  if (hasAssociations) return true;
  //has about info
  if (type === KitType.VAGINAL && bacterium.about_vaginal) return true;
  if (type !== KitType.VAGINAL && bacterium.about) return true; // Check general 'about' for non-vaginal
  if (bacterium.about) return true; // Fallback for any kit type if specific one isn't present but general is

  return false;
}

type BacteriumProps = {
  bacterium: BacteriumType;
  transferredFromBirthMother?: boolean;
  type?: KitType;
};

export const Bacterium = ({
  bacterium,
  transferredFromBirthMother = false,
  type = KitType.STOOL,
}: BacteriumProps) => {
  const [isOpen, setIsOpen] = useState(false);
  // not all bacteria have an about
  const currentAboutText =
    type === KitType.VAGINAL && bacterium?.about_vaginal
      ? bacterium.about_vaginal
      : bacterium?.about;
  const source = currentAboutText?.replace(/\\n/gi, '\n \n');

  const nameToDisplay =
    type === KitType.VAGINAL && bacterium.tn
      ? bacterium.tn
      : bacterium.taxonomy_name;

  const associations = useMemo(() => {
    const modernAssociations =
      bacterium.report_taxa_categories?.gut_vaginal_health?.flatMap(
        gvh => gvh.gut_vaginal_health_associations || [],
      ) || [];

    const legacyAssociations = (
      (bacterium.report_taxa_categories as any)?.associations_list || []
    ).map((association: any) => ({
      ...association,
      association_id: association.id,
      association_name: association.display_name,
      conditions_metrics: association.conditions_metrics,
    }));

    return [...modernAssociations, ...legacyAssociations];
  }, [bacterium.report_taxa_categories]);

  const category =
    type === KitType.VAGINAL ? bacterium.category_vaginal : bacterium.category;

  return (
    <>
      <div
        id={bacterium.rtc_id + '' || bacterium.id}
        className={c(
          styles.row,
          detailsPresent(bacterium, type) && styles.hoverable,
          detailsPresent(bacterium, type) && 'hoverable',
          styles[category ?? 'Unknown'],
        )}
        onClick={() => setIsOpen(!isOpen)}
        role={detailsPresent(bacterium, type) ? 'button' : 'listitem'}
      >
        <div className={styles.taxonomyName}>{nameToDisplay}</div>
        <div className={styles.bacteriaDetails}>
          <div className={c('font-S', styles.bacteriaAbundance)}>
            {(bacterium.classified_relative_abundance
              ? bacterium.classified_relative_abundance * 100
              : 0
            ).toFixed(3)}
            %
          </div>

          <Icon
            name='circleFilled'
            size='s'
            className={c(
              'my-auto',
              styles.bacteriaCircle,
              styles[category ?? ''] ?? styles.Unknown,
            )}
          />
          {!!transferredFromBirthMother && (
            // content is coming from css
            <div
              className={`ps-1 sharedTaxaSymbol present-in-${bacterium.shared}`}
            />
          )}

          {(
            type === KitType.VAGINAL ? bacterium.about_vaginal : bacterium.about
          ) ? (
            <Icon
              name='chevronDown'
              size='s'
              data-testid='chevron'
              className={c(
                'my-auto',
                styles.rotate,
                isOpen ? styles.iconRotateUp : styles.iconRotateDown,
              )}
            />
          ) : (
            <div className={styles.spacer} />
          )}
        </div>
      </div>

      <AnimatePresence>
        {detailsPresent(bacterium, type) && isOpen && (
          <motion.div
            initial={{ height: 0 }}
            animate={{ height: 'auto' }}
            exit={{ height: 0 }}
            transition={{ duration: 0.6 }}
            className={styles.collapse}
          >
            <div className={c(styles.bacteriaDetail, 'text-start')}>
              {!!source && (
                <>
                  <h5
                    className={styles.detailHeading}
                    data-test='bacteria-collapse-about'
                  >
                    About
                  </h5>
                  <div>
                    <Markdown>{source}</Markdown>
                  </div>
                </>
              )}
              {associations.length > 0 && (
                <AssociationsList associations={associations} />
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
